import { Injectable } from '@angular/core';
import { TeamFilterResult } from 'src/app/shared/models/team/teamFilterResult';
import { Team } from 'src/app/shared/models/team/team';
import {
  FilterGroup,
  ODataFilterValues,
  ODataSubFilter
} from '../../shared/components/odata-filter/models/odata-filter';
import { TeamRegularFields } from './team-regular-fields';
import { AttributeDefinition } from '../../shared/models/attributes/attributeDefinition';
import { AttributeEntityType } from '../../shared/models/attributes/attributeEntityType';
import { BaseODataService } from 'src/app/shared/services/base-odata.service';
import { CustomFieldFilterModel } from '../../shared/odata-filter-models/custom-field-filter-model';
import {
  TEAM_MEMBER_CREDENTIALS_SUB_FILTER_MODELS,
  TEAM_CREDENTIALS_SUB_FILTER_MODELS,
  TEAM_SYSTEM_FILTER_MODELS
} from 'src/app/shared/components/odata-filter/models/system-filter-models';
import { ViewContext } from 'src/app/shared/components/odata-filter/models/view-context';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { FilterOperatorType } from 'src/app/shared/components/odata-filter/models/filter-operator-type';
import { TeamStatus } from '../../shared/models/team/teamStatus';
import { map } from 'rxjs/operators';
import { TenantModuleType } from 'src/app/shared/models/tenantModuleType';
import { ModuleGuardService } from 'src/app/guards/module-guard.service';
import { AdminCredentialsService } from 'src/app/admin/admin-home/admin-credentials/admin-credentials.service';
import { CredentialDefinitionType } from 'src/app/shared/models/credential/credential-definition.model';
import { LookupService } from 'src/shared-app-portal/services/lookup.service';

@Injectable({
  providedIn: 'root'
})
export class TeamFilterService extends BaseODataService {
  constructor(
    private moduleGuardService: ModuleGuardService,
    private adminCredentialsService: AdminCredentialsService,
    private lookupService: LookupService
  ) {
    super();

    this._baseUrl = 'teams';
    this._searchTextModel = TEAM_SYSTEM_FILTER_MODELS.TeamNameFilterModel;
    this._filterResult = new TeamFilterResult();
    this._viewContext = ViewContext.Teams;

    this._filterCollection = [
      TEAM_SYSTEM_FILTER_MODELS.TeamNameFilterModel,
      TEAM_SYSTEM_FILTER_MODELS.TeamStatusFilterModel,
      TEAM_SYSTEM_FILTER_MODELS.TeamStartDateFilterModel,
      TEAM_SYSTEM_FILTER_MODELS.TeamEndDateFilterModel,
      TEAM_SYSTEM_FILTER_MODELS.TeamPublicNameFilterModel,
      TEAM_SYSTEM_FILTER_MODELS.TeamCityFilterModel,
      TEAM_SYSTEM_FILTER_MODELS.TeamStateFilterModel,
      TEAM_SYSTEM_FILTER_MODELS.TeamCountyFilterModel,
      TEAM_SYSTEM_FILTER_MODELS.TeamCountryFilterModel
    ];
    this.setCustomFields();
    this.setGeoFilters();

    if (this.moduleGuardService.hasModule(TenantModuleType.Credentials)) {
      this.setCredentialFilters();
    }

    this.workspaceService.currentWorkspaces.subscribe((result) => {
      if (!result || !result.workspaces.length || result.activeWorkspace.id != null) return;
      if (result.workspaces.length == 1 && result.workspaces[0].id == null) return; // if the only workspace is org.
      let teamWorkspaceFilterModel = TEAM_SYSTEM_FILTER_MODELS.TeamWorkspaceFilterModel;
      teamWorkspaceFilterModel.values = result.workspaces.map(
        (x) => new ODataFilterValues(x.id, x.name)
      );
      if (this.systemFilterIndex(teamWorkspaceFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(teamWorkspaceFilterModel)].values =
          teamWorkspaceFilterModel.values;
      } else {
        this._filterCollection.push(teamWorkspaceFilterModel);
      }
    });
  }

  protected setOrderBy(value: string[]) {
    if (!value.find((e) => e.includes(TeamRegularFields.id))) {
      value.push(TeamRegularFields.id);
    }
    super.setOrderBy(value);
  }

  applyExistingFilters() {
    this.applyFilter(this._appliedFilters);
  }

  removeDeletedTeamFromFilteredResult(teamId: string) {
    let filteredIndex = this._filterResult.values.findIndex((t) => t.id == teamId);
    if (filteredIndex > -1) {
      this._filterResult.values.splice(filteredIndex, 1);
      this.filterResultValues.next(this._filterResult.values);
    }
  }

  updateTeamFromFilteredResult(updatedTeam: Team, teamId: string) {
    let filteredIndex = this._filterResult.values.findIndex((t) => t.id == teamId);
    let parsedTeam = Team.parse(updatedTeam);

    if (
      this._appliedFilters.findIndex((x) => this.hasActiveFilter(x)) > -1 &&
      parsedTeam.isArchived
    ) {
      this.removeDeletedTeamFromFilteredResult(teamId);
      return;
    }

    if (
      this._appliedFilters.findIndex((x) => this.hasArchivedFilter(x)) > -1 &&
      !parsedTeam.isArchived
    ) {
      this.removeDeletedTeamFromFilteredResult(teamId);
      return;
    }
    this._filterResult.values[filteredIndex] = parsedTeam;
    this.filterResultValues.next(this._filterResult.values);
  }

  private setCustomFields() {
    this.attributeService.attributes
      .pipe(
        map((attributes) => {
          if (!attributes) return null;
          return attributes.filter((a) => a.filterable);
        })
      )
      .subscribe((result: AttributeDefinition[]) => {
        if (result && result.length > 0) {
          let teamCustomFields = result.filter(
            (field) =>
              field.entityType == AttributeEntityType.Team &&
              !this._filterCollection.find(
                (x) => x.displayName == field.name && x.targetFieldId == field.id
              )
          );

          teamCustomFields.forEach((item) => {
            const searchValueType = AttributeDefinition.getSearchValueType(item);
            if (!searchValueType) {
              return;
            }

            let customFieldFilterModel = new CustomFieldFilterModel(
              FilterGroup.TeamCustomFields,
              item.name,
              TeamRegularFields.customFields,
              item.id,
              searchValueType,
              item.valueList.map((x) => new ODataFilterValues(x.id, x.itemValue))
            );
            this._filterCollection.push(customFieldFilterModel);
          });
        }
        this.filterCollection.next(this._filterCollection);
      });
  }

  private setCredentialFilters() {
    let credentialFilterModel = TEAM_SYSTEM_FILTER_MODELS.TeamCredentialFilterModel;
    let teamMemberCredentialViewFilterModel =
      TEAM_SYSTEM_FILTER_MODELS.TeamMemberCredentialViewFilterModel;
    let activeTeamMemberCredentialViewFilterModel =
      TEAM_SYSTEM_FILTER_MODELS.ActiveTeamMemberCredentialViewFilterModel;
    if (this.systemFilterIndex(credentialFilterModel) == -1) {
      this.adminCredentialsService
        .getAllCredentialsNames(CredentialDefinitionType.Team)
        .then(async (credentials) => {
          credentialFilterModel.values = credentials.map(
            (x) => new ODataFilterValues(x.id, x.name)
          );

          credentialFilterModel.subFilter = new ODataSubFilter([
            TEAM_CREDENTIALS_SUB_FILTER_MODELS.IssueDateFilterModel,
            TEAM_CREDENTIALS_SUB_FILTER_MODELS.ExpirationDateFilterModel,
            TEAM_CREDENTIALS_SUB_FILTER_MODELS.CredentialStatusFilterModel,
            TEAM_CREDENTIALS_SUB_FILTER_MODELS.CredentialNumberFilterModel
          ]);

          const specialties = await this.adminCredentialsService.getAllSpecialties(
            CredentialDefinitionType.Team
          );
          if (specialties && specialties.length > 0) {
            const specialtyFilterModel =
              TEAM_CREDENTIALS_SUB_FILTER_MODELS.CredentialSpecialtyFilterModel;
            specialtyFilterModel.values = specialties.map(
              (s) => new ODataFilterValues(s.id, s.name)
            );
            credentialFilterModel.subFilter.filters.push(specialtyFilterModel);
          }

          this._filterCollection.push(credentialFilterModel);
        });
    }

    if (
      this.systemFilterIndex(teamMemberCredentialViewFilterModel) == -1 ||
      this.systemFilterIndex(activeTeamMemberCredentialViewFilterModel) == -1
    ) {
      this.adminCredentialsService
        .getAllCredentialsNames(CredentialDefinitionType.Person)
        .then(async (credentials) => {
          teamMemberCredentialViewFilterModel.values = credentials.map(
            (x) => new ODataFilterValues(x.id, x.name)
          );
          activeTeamMemberCredentialViewFilterModel.values = credentials.map(
            (x) => new ODataFilterValues(x.id, x.name)
          );

          teamMemberCredentialViewFilterModel.subFilter = new ODataSubFilter([
            TEAM_MEMBER_CREDENTIALS_SUB_FILTER_MODELS.PersonFullNameFilterModel,
            TEAM_MEMBER_CREDENTIALS_SUB_FILTER_MODELS.PersonEmailFilterModel,
            TEAM_MEMBER_CREDENTIALS_SUB_FILTER_MODELS.IssueDateFilterModel,
            TEAM_MEMBER_CREDENTIALS_SUB_FILTER_MODELS.ExpirationDateFilterModel,
            TEAM_MEMBER_CREDENTIALS_SUB_FILTER_MODELS.CredentialStatusFilterModel,
            TEAM_MEMBER_CREDENTIALS_SUB_FILTER_MODELS.CredentialNumberFilterModel
          ]);

          const specialties = await this.adminCredentialsService.getAllSpecialties(
            CredentialDefinitionType.Person
          );
          if (specialties && specialties.length > 0) {
            const specialtyFilterModel =
              TEAM_MEMBER_CREDENTIALS_SUB_FILTER_MODELS.CredentialSpecialtyFilterModel;
            specialtyFilterModel.values = specialties.map(
              (s) => new ODataFilterValues(s.id, s.name)
            );
            teamMemberCredentialViewFilterModel.subFilter.filters.push(specialtyFilterModel);
          }
          if (this.systemFilterIndex(activeTeamMemberCredentialViewFilterModel) == -1) {
            this._filterCollection.push(activeTeamMemberCredentialViewFilterModel);
          }
          if (this.systemFilterIndex(teamMemberCredentialViewFilterModel) == -1) {
            this._filterCollection.push(teamMemberCredentialViewFilterModel);
          }
        });
    }
  }

  private hasActiveFilter(appliedFilter: SelectedFilter): boolean {
    return (
      appliedFilter.filter.targetField == TeamRegularFields.status &&
      ((appliedFilter.filterOperator.displayOperator == FilterOperatorType.eq &&
        appliedFilter.searchValue == TeamStatus.Active) ||
        (appliedFilter.filterOperator.displayOperator == FilterOperatorType.ne &&
          appliedFilter.searchValue == TeamStatus.Archived))
    );
  }
  private hasArchivedFilter(appliedFilter: SelectedFilter): boolean {
    return (
      appliedFilter.filter.targetField == TeamRegularFields.status &&
      ((appliedFilter.filterOperator.displayOperator == FilterOperatorType.eq &&
        appliedFilter.searchValue == TeamStatus.Archived) ||
        (appliedFilter.filterOperator.displayOperator == FilterOperatorType.ne &&
          appliedFilter.searchValue == TeamStatus.Active))
    );
  }

  private setGeoFilters() {
    this.lookupService.countries.subscribe((result) => {
      if (!result) return;
      let countryFilterModel = TEAM_SYSTEM_FILTER_MODELS.TeamCountryFilterModel;
      countryFilterModel.values = result.map((x) => new ODataFilterValues(x.name, x.name));
      if (this.systemFilterIndex(countryFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(countryFilterModel)].values =
          countryFilterModel.values;
      } else {
        this._filterCollection.push(countryFilterModel);
      }
    });

    this.lookupService.states.subscribe((result) => {
      if (!result) return;
      let stateFilterModel = TEAM_SYSTEM_FILTER_MODELS.TeamStateFilterModel;
      stateFilterModel.values = result.map((x) => new ODataFilterValues(x.name, x.name));
      if (this.systemFilterIndex(stateFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(stateFilterModel)].values =
          stateFilterModel.values;
      } else {
        this._filterCollection.push(stateFilterModel);
      }
    });
  }
}
